import * as Api from '@ViewModels';
import { StyleDeclarationValue } from 'aphrodite';
import { styleSheet } from './styles';

type CampaignData = Api.IAutomationReportView | Api.IAutomationReport | Api.IAutomation;

const getLatestCommunicationStep = (
	report: Api.IAutomationReportView
): Api.IAutomationStepStatusReportView<Api.IAutomationStep> | null => {
	const processStatus: Api.ProcessStatus | undefined = report?.status?.status;
	const steps = report?.steps?.filter(x => {
		const isCommStep = x.step._type === 'EmailAutomationStep' || x.step._type === 'TextingAutomationStep';
		if (!isCommStep) {
			return x.step._type === 'SwitchAutomationStep';
		}
		return isCommStep;
	});

	if (!steps || steps.length === 0) {
		return null;
	}

	const sortedSteps = steps.sort((x, y) => {
		const xDate = new Date(x.dueDate);
		const yDate = new Date(y.dueDate);
		if (xDate.getTime() === yDate.getTime()) {
			return 0;
		}
		return xDate.getTime() < yDate.getTime() ? -1 : 1;
	});

	switch (processStatus) {
		case Api.ProcessStatus.Completed: {
			return sortedSteps.find(
				x =>
					x.status.status === Api.ProcessStatus.Completed ||
					x.status.status === Api.ProcessStatus.Started ||
					x.status.status === Api.ProcessStatus.Failed ||
					x.status.status === Api.ProcessStatus.Skipped
			);
		}
		case Api.ProcessStatus.Cancelled:
		case Api.ProcessStatus.Failed:
		case Api.ProcessStatus.Skipped: {
			return sortedSteps.find(
				x =>
					x.status.status === Api.ProcessStatus.Cancelled ||
					x.status.status === Api.ProcessStatus.Failed ||
					x.status.status === Api.ProcessStatus.Skipped
			);
		}
		case Api.ProcessStatus.Queued:
		case Api.ProcessStatus.Started: {
			return sortedSteps.find(
				x =>
					x.status.status === Api.ProcessStatus.Completed ||
					x.status.status === Api.ProcessStatus.Started ||
					x.status.status === Api.ProcessStatus.Queued ||
					x.status.status === Api.ProcessStatus.Skipped ||
					x.status.status === Api.ProcessStatus.Cancelled
			);
		}
		default: {
			return null;
		}
	}
};

export const getCampaignStatusValues = (campaign: CampaignData) => {
	let latestStep: Api.IAutomationStepStatusReportView<Api.IAutomationStep> = null;
	let processStatus: Api.ProcessStatus = null;
	let campaignStatus: Api.IAutomationStatus = null;
	let dueDate: string = null;

	// Check if this is an IAutomationReport (has automationStep property)
	const isAutomationReport = 'automationStep' in campaign;

	if (isAutomationReport) {
		// IAutomationReport - use direct status and dueDate
		processStatus = campaign?.status?.status;
		campaignStatus = campaign.status;
		dueDate = (campaign as Api.IAutomationReport).dueDate;
	} else {
		// IAutomationReportView or IAutomation - compute from steps
		latestStep = getLatestCommunicationStep(campaign as Api.IAutomationReportView);
		processStatus = latestStep?.status?.status || (campaign as Api.IAutomation)?.status?.status;
		campaignStatus = (campaign as Api.IAutomation)?.status || (campaign as Api.IAutomationReportView)?.status;
		dueDate = latestStep?.dueDate;
	}

	let colorStyle: StyleDeclarationValue = null;
	let dateString = '';
	let datePrefix = 'for';
	let statusText: string = processStatus;

	switch (processStatus) {
		case Api.ProcessStatus.Completed: {
			colorStyle = styleSheet.green;
			dateString = campaignStatus?.completedDate;
			datePrefix = 'on';
			statusText = 'Sent';
			break;
		}
		case Api.ProcessStatus.Cancelled:
		case Api.ProcessStatus.Failed:
		case Api.ProcessStatus.Skipped: {
			colorStyle = styleSheet.red;
			dateString =
				latestStep?.status?.cancelledDate ||
				latestStep?.status?.skippedDate ||
				latestStep?.status?.errorDate ||
				campaignStatus?.cancelledDate ||
				campaignStatus?.skippedDate ||
				campaignStatus?.errorDate;
			datePrefix = 'on';
			break;
		}
		case Api.ProcessStatus.Started: {
			colorStyle = styleSheet.blue;
			dateString = latestStep?.status?.startedDate || campaignStatus?.startedDate;
			datePrefix = 'on';
			break;
		}
		case Api.ProcessStatus.Queued: {
			colorStyle = styleSheet.blue;
			dateString = dueDate;
			statusText = 'Scheduled';
			break;
		}
		default: {
			break;
		}
	}

	return {
		colorStyle,
		datePrefix,
		dateString,
		processStatus,
		statusText,
	};
};
